import { Database } from 'libs/supabaseTypes';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ProfileData, supabase, useAuth } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

import { Fonts, spacing } from 'styles/theme';
import {
    Bumper,
    Card,
    ContentScroller,
    Divider,
    GridListWrapper,
    HorizontalFlexWrapper,
} from 'styles/shared';
import { Button } from 'components/ui/Button';
import { GroupCard } from 'components/ui/GroupCard';

type GroupData = Database['public']['Tables']['groups']['Row'];
type GroupSort = 'next_session_date' | 'gid';

export const GroupList = () => {
    const { userProfile } = useAuth();
    const { showErrorToast } = useToast();

    const [, setSearchParams] = useSearchParams();

    const [coachProfileData, setCoachProfileData] =
        useState<Array<ProfileData> | null>([]);
    const [groupsData, setGroupsData] = useState<Array<GroupData> | null>([]);
    const [groupSort, setGroupSort] = useState<GroupSort>('next_session_date');

    useEffect(() => {
        const getCoachProfiles = async () => {
            const { data, error } = await supabase
                .from('profiles')
                .select()
                .eq('is_coach', true);

            if (error == null) {
                setCoachProfileData(data);
            } else {
                showErrorToast(error.message);
            }
        };
        if (userProfile != null) {
            getCoachProfiles();
        }
    }, [showErrorToast, userProfile]);

    useEffect(() => {
        const getGroupsData = async () => {
            const { data, error } = await supabase
                .from('groups')
                .select()
                .order('next_session_date', { ascending: false });

            if (error == null) {
                setGroupsData(data);
            } else {
                showErrorToast(error.message);
            }
        };
        if (userProfile != null) {
            getGroupsData();
        }
    }, [showErrorToast, userProfile]);

    const sortByGid = useCallback((groupA: GroupData, groupB: GroupData) => {
        if (groupA.gid < groupB.gid) {
            return -1;
        }
        if (groupA.gid > groupB.gid) {
            return 1;
        }
        return 0;
    }, []);

    const sortedGroupsData = useMemo(
        () =>
            groupSort === 'next_session_date'
                ? // If next_session_date is null, move those groups to the end
                  // and sort them by gid
                  structuredClone(groupsData)?.sort(
                      (groupA: GroupData, groupB: GroupData) => {
                          if (
                              groupA.next_session_date != null &&
                              groupB.next_session_date == null
                          ) {
                              return -1;
                          }
                          if (
                              groupA.next_session_date == null &&
                              groupB.next_session_date != null
                          ) {
                              return 1;
                          }
                          if (
                              groupA.next_session_date == null &&
                              groupB.next_session_date == null
                          ) {
                              return sortByGid(groupA, groupB);
                          }
                          return 0;
                      }
                  )
                : structuredClone(groupsData)?.sort(sortByGid),
        [groupSort, groupsData, sortByGid]
    );

    const onUpdateGroupSort = useCallback(
        (sort: GroupSort) => () => {
            setGroupSort(sort);
        },
        []
    );

    const onOpenGroupDetails = useCallback(
        (groupId: string) => () => {
            setSearchParams({
                modalContent: 'group-details',
                groupId,
            });
        },
        [setSearchParams]
    );

    return (
        <ContentScroller>
            <Bumper />
            <Bumper />
            <Bumper />
            <Bumper />
            <Card>
                <HorizontalFlexWrapper>
                    <Button
                        onClick={onUpdateGroupSort('next_session_date')}
                        $style={
                            groupSort === 'next_session_date'
                                ? 'outline'
                                : 'float'
                        }
                        $isSmall
                    >{`Sort${
                        groupSort === 'next_session_date' ? 'ed' : ''
                    } by session date`}</Button>
                    <Bumper $minWidth={spacing.small} />
                    <Button
                        onClick={onUpdateGroupSort('gid')}
                        $style={groupSort === 'gid' ? 'outline' : 'float'}
                        $isSmall
                    >{`Sort${
                        groupSort === 'gid' ? 'ed' : ''
                    } alphabetically`}</Button>
                </HorizontalFlexWrapper>
                <Bumper />
                <Fonts.Heading6>{`Your groups:`}</Fonts.Heading6>
                <Bumper $minHeight={spacing.small} />
                <GridListWrapper $gap={spacing.large}>
                    {sortedGroupsData
                        ?.filter((group: GroupData) =>
                            userProfile?.coach_groups?.includes(group.gid)
                        )
                        .map((group: GroupData) => {
                            const coachProfile = coachProfileData?.find(
                                (profile: ProfileData) =>
                                    profile.coach_groups?.includes(group.gid)
                            );
                            return (
                                <GroupCard
                                    key={group.gid}
                                    group={group}
                                    userProfile={userProfile}
                                    coachProfile={coachProfile}
                                    onClick={onOpenGroupDetails}
                                />
                            );
                        })}
                </GridListWrapper>
                <Bumper />
                <Divider />
                <Bumper $minHeight={spacing.xxSmall} />
                <Fonts.Heading6>{`Other groups:`}</Fonts.Heading6>
                <Bumper $minHeight={spacing.small} />
                <GridListWrapper $gap={spacing.large}>
                    {sortedGroupsData
                        ?.filter(
                            (group: GroupData) =>
                                !userProfile?.coach_groups?.includes(group.gid)
                        )
                        .map((group: GroupData) => {
                            const coachProfile = coachProfileData?.find(
                                (profile: ProfileData) =>
                                    profile.coach_groups?.includes(group.gid)
                            );
                            return (
                                <GroupCard
                                    key={group.gid}
                                    group={group}
                                    userProfile={userProfile}
                                    coachProfile={coachProfile}
                                    onClick={onOpenGroupDetails}
                                />
                            );
                        })}
                </GridListWrapper>
            </Card>
        </ContentScroller>
    );
};
